import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import QRCode from "qrcode";
import { authenticator } from "otplib";
import "./AppStyles.css";

// Configure the authenticator
authenticator.options = {
  digits: 6,
  window: 1,
};

const TwoFactorAuth = ({
  onVerified,
  setupMode = false,
  userEmail,
  preGeneratedSecret = null,
  skipSecretGeneration = false,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState(preGeneratedSecret || "");
  const [showQrCode, setShowQrCode] = useState(!!preGeneratedSecret);
  const [isLoading, setIsLoading] = useState(false);
  const auth = getAuth();
  const user = auth.currentUser;

  // If a pregenerated secret was provided, set up the QR code
  useEffect(() => {
    if (preGeneratedSecret && !qrCodeUrl) {
      const otpauth = authenticator.keyuri(
        userEmail || "user@createvista.com",
        "CreateVista",
        preGeneratedSecret
      );

      QRCode.toDataURL(otpauth, (err, imageUrl) => {
        if (err) {
          setError("Could not generate QR code");
          return;
        }
        setQrCodeUrl(imageUrl);
        setShowQrCode(true);
      });
    }
  }, [preGeneratedSecret, qrCodeUrl, userEmail]);

  // Generate a new secret for the user when setting up 2FA
  const generateSecret = async () => {
    try {
      setIsLoading(true);
      // Skip generation if we already have a pregenerated secret
      if (skipSecretGeneration && preGeneratedSecret) {
        setShowQrCode(true);
        setIsLoading(false);
        return;
      }

      // Generate a base32 encoded secret
      let newSecret;
      try {
        newSecret = authenticator.generateSecret();
        console.log("Generated new secret:", newSecret);
      } catch (secretError) {
        console.error("Error generating secret:", secretError);
        setError(
          "Failed to generate authentication secret: " + secretError.message
        );
        setIsLoading(false);
        return;
      }

      setSecret(newSecret);

      // Generate QR code for Google Authenticator regardless of user auth state
      const email = user?.email || userEmail || "user@createvista.com";
      const otpauth = authenticator.keyuri(email, "CreateVista", newSecret);

      console.log("Generated otpauth URL:", otpauth);

      // Generate QR code from the otpauth URL
      QRCode.toDataURL(otpauth, (err, imageUrl) => {
        if (err) {
          console.error("QR code generation error:", err);
          setError("Could not generate QR code: " + err.message);
          setIsLoading(false);
          return;
        }
        console.log("QR code generated successfully");
        setQrCodeUrl(imageUrl);
        setShowQrCode(true);
        setIsLoading(false);
      });

      // Save to Firebase if user is authenticated
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            await updateDoc(userRef, {
              twoFactorSecret: newSecret,
              twoFactorEnabled: true,
            });
          } else {
            await setDoc(userRef, {
              email: user.email,
              twoFactorSecret: newSecret,
              twoFactorEnabled: true,
            });
          }
          console.log("Successfully saved 2FA secret to Firebase");
        } catch (firebaseError) {
          console.error("Firebase error:", firebaseError);
          setError(
            "Failed to save authentication secret to database: " +
              firebaseError.message
          );
          // Don't set isLoading to false here as we still want to show the QR code
        }
      } else {
        console.log(
          "User not authenticated, QR code generated but not saved to Firebase"
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error in generateSecret:", error);
      setError(
        "Failed to generate authentication secret. Please try again. Details: " +
          error.message
      );
    }
  };

  // Handle verification code submission
  const handleVerify = async () => {
    setError(null);
    setIsLoading(true);
    
    console.log("2FA Verification attempt:", {
      setupMode,
      hasUser: !!user,
      hasPreGeneratedSecret: !!preGeneratedSecret,
      userEmail,
      verificationCode: verificationCode.length
    });

    try {
      let secretToVerify = secret;
      // eslint-disable-next-line no-unused-vars
      let userIdToCheck = user?.uid;

      // For users that are in the process of setting up 2FA who aren't currently authenticated
      if ((!user || !setupMode) && preGeneratedSecret) {
        console.log("Using pregenerated secret for verification");
        secretToVerify = preGeneratedSecret;
      } else if (!setupMode && !user && userEmail) {
        // This is the 2FA verification step - user is not authenticated yet
        // Try to get secret from sessionStorage first (temporarily stored during login)
        console.log("Getting secret from sessionStorage or Firestore for 2FA verification (no auth)");
        
        const tempSecret = sessionStorage.getItem("temp_2fa_secret");
        if (tempSecret) {
          console.log("Found temporary 2FA secret from sessionStorage");
          secretToVerify = tempSecret;
        } else {
          // Fallback to Firestore (this will likely fail due to permissions)
          console.log("No temporary secret found, trying Firestore");
          const pendingUserId = localStorage.getItem("2fa_pending_userid");
          if (pendingUserId) {
            const userRef = doc(db, "users", pendingUserId);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists() && userDoc.data().twoFactorSecret) {
              secretToVerify = userDoc.data().twoFactorSecret;
              console.log("Found 2FA secret for user");
            } else {
              setError("Two-factor authentication is not set up for this account");
              setIsLoading(false);
              return;
            }
          } else {
            setError("Invalid verification session");
            setIsLoading(false);
            return;
          }
        }
      } else if (!setupMode && user) {
        // Get the user's secret from Firestore for normal verification (user is authenticated)
        console.log("Getting secret for authenticated user during 2FA");
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists() && userDoc.data().twoFactorSecret) {
          secretToVerify = userDoc.data().twoFactorSecret;
          console.log("Found 2FA secret for authenticated user");
        } else {
          setError("Two-factor authentication is not set up for this account");
          setIsLoading(false);
          return;
        }
      }

      console.log("Verifying code with secret");
      
      // Verify the code
      const isValid = authenticator.verify({
        token: verificationCode,
        secret: secretToVerify,
      });

      if (isValid) {
        console.log("2FA verification successful");
        
        // Clean up temporary secret
        sessionStorage.removeItem("temp_2fa_secret");
        
        setIsLoading(false); // Set loading to false before callback
        if (onVerified) {
          onVerified();
        }
      } else {
        console.log("2FA verification failed - invalid code");
        setError("Invalid verification code");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Verification error:", error);
      setError("Verification failed. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="two-factor-container">
      <h2>
        {setupMode
          ? "Set Up Two-Factor Authentication"
          : "Two-Factor Authentication"}
      </h2>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing...</p>
        </div>
      )}

      {setupMode && !showQrCode && (
        <div>
          <p>
            Enable two-factor authentication to add an extra layer of security
            to your account.
          </p>
          <button onClick={generateSecret} disabled={isLoading}>
            Set Up
          </button>
        </div>
      )}

      {showQrCode && (
        <div className="qr-container">
          <p>Scan this QR code with Google Authenticator:</p>
          {qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt="QR Code"
              style={{ width: "200px", height: "200px" }}
            />
          ) : (
            <p>QR code loading...</p>
          )}
          <p>
            Or manually enter this code: <strong>{secret}</strong>
          </p>
          <p>
            After scanning, enter the 6-digit code from Google Authenticator
            below:
          </p>
        </div>
      )}

      {(!setupMode || showQrCode) && (
        <div>
          {!setupMode && (
            <p>Enter the 6-digit code from your Google Authenticator app:</p>
          )}
          <div className="verification-input">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="6-digit code"
              maxLength={6}
              disabled={isLoading}
            />
            <button onClick={handleVerify} disabled={isLoading}>
              Verify
            </button>
          </div>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default TwoFactorAuth;
